import React, { useMemo, useState, useRef } from 'react';
import { ReceiptEuro, Plus, Trash2, Edit, Search, Check, X, ScanLine, Loader2, Paperclip, Eye, Upload } from 'lucide-react';
import { useData } from '../context/DataContext';
import { carsysApi } from '../api/carsysApi';
import { confirmDialog } from '../lib/confirm';
import { toast } from 'sonner';
import { useT } from '../context/LanguageContext';

const eur = (n) => (Number(n) || 0).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' });

export const KOSTEN_CATEGORIEEN = [
  'Onderdelen', 'Gereedschap', 'Pand / Huur', 'Energie', 'Verzekering',
  'Voertuigkosten', 'Marketing', 'Administratie', 'Telefoon / Internet', 'Overig',
];

const leegFormulier = () => ({
  datum: new Date().toISOString().slice(0, 10),
  leverancier: '',
  categorie: 'Onderdelen',
  omschrijving: '',
  factuurnummer: '',
  bedragExcl: '',
  btwPercentage: 21,
  betaald: false,
  betaaldatum: '',
});

export default function Kosten() {
  const { t } = useT();
  const { kosten, createKost, updateKost, deleteKost, refreshAll } = useData();
  const [filter, setFilter] = useState('');
  const [catFilter, setCatFilter] = useState('alle');
  const [addOpen, setAddOpen] = useState(false);
  const [editKost, setEditKost] = useState(null);
  const [form, setForm] = useState(leegFormulier());
  const [selectedId, setSelectedId] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [vanDatum, setVanDatum] = useState('');
  const [totDatum, setTotDatum] = useState('');
  const [alleenBijlage, setAlleenBijlage] = useState(false);
  const [scannedImage, setScannedImage] = useState(null);
  const [bijlageFile, setBijlageFile] = useState(null);
  const [bijlageBusy, setBijlageBusy] = useState(false);
  const fileInputRef = useRef(null);
  const bijlageInputRef = useRef(null);

  const handleScan = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error(t('Bestand te groot (max 10MB)', 'Bestand te groot (max 10MB)')); return; }
    setScanning(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const res = await carsysApi.scanBon(reader.result);
        setForm({
          ...leegFormulier(),
          datum: (res.datum && /^\d{4}-\d{2}-\d{2}$/.test(res.datum)) ? res.datum : new Date().toISOString().slice(0, 10),
          leverancier: res.leverancier || '',
          categorie: KOSTEN_CATEGORIEEN.includes(res.categorie) ? res.categorie : 'Overig',
          omschrijving: res.omschrijving || '',
          factuurnummer: res.factuurnummer || '',
          bedragExcl: res.bedragExcl || '',
          btwPercentage: [0, 9, 21].includes(Number(res.btwPercentage)) ? Number(res.btwPercentage) : 21,
        });
        setScannedImage(reader.result);
        setBijlageFile(null);
        setAddOpen(true);
        const bt = res.betrouwbaarheid ? ` (${t('betrouwbaarheid', 'betrouwbaarheid')}: ${res.betrouwbaarheid})` : '';
        toast.success(t('Bon ingelezen — controleer en sla op', 'Bon ingelezen — controleer en sla op') + bt);
      } catch (err) {
        toast.error(t('Bon scannen mislukt', 'Bon scannen mislukt'), { description: err.response?.data?.detail || err.message });
      } finally { setScanning(false); }
    };
    reader.onerror = () => { setScanning(false); toast.error(t('Kon bestand niet lezen', 'Kon bestand niet lezen')); };
    reader.readAsDataURL(file);
  };

  const jaar = new Date().getFullYear();

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return [...kosten]
      .sort((a, b) => (b.datum || '').localeCompare(a.datum || ''))
      .filter(k => catFilter === 'alle' || k.categorie === catFilter)
      .filter(k => !vanDatum || (k.datum || '') >= vanDatum)
      .filter(k => !totDatum || (k.datum || '') <= totDatum)
      .filter(k => !alleenBijlage || !!k.bijlage)
      .filter(k => !f
        || (k.leverancier || '').toLowerCase().includes(f)
        || (k.omschrijving || '').toLowerCase().includes(f)
        || (k.factuurnummer || '').toLowerCase().includes(f)
        || (k.categorie || '').toLowerCase().includes(f));
  }, [kosten, filter, catFilter, vanDatum, totDatum, alleenBijlage]);

  const kostenDitJaar = useMemo(() => kosten.filter(k => (k.datum || '').slice(0, 4) === String(jaar)), [kosten, jaar]);
  const totaalIncl = kostenDitJaar.reduce((s, k) => s + (Number(k.bedragIncl) || 0), 0);
  const totaalBtw = kostenDitJaar.reduce((s, k) => s + (Number(k.btwBedrag) || 0), 0);
  const onbetaald = kosten.filter(k => !k.betaald);
  const onbetaaldBedrag = onbetaald.reduce((s, k) => s + (Number(k.bedragIncl) || 0), 0);

  // Live preview van BTW/totaal in formulier
  const preview = (excl, pct) => {
    const e = parseFloat(excl) || 0;
    const btw = Math.round(e * (parseFloat(pct) || 0)) / 100;
    return { btw, incl: Math.round((e + btw) * 100) / 100 };
  };

  const handleSave = async () => {
    const excl = parseFloat(form.bedragExcl);
    if (!form.leverancier.trim()) { toast.error(t('Vul een leverancier in', 'Vul een leverancier in')); return; }
    if (!excl || excl <= 0) { toast.error(t('Vul een geldig bedrag (excl. BTW) in', 'Vul een geldig bedrag (excl. BTW) in')); return; }
    try {
      const r = await createKost({
        datum: form.datum,
        leverancier: form.leverancier.trim(),
        categorie: form.categorie,
        omschrijving: form.omschrijving.trim(),
        factuurnummer: form.factuurnummer.trim(),
        bedragExcl: excl,
        btwPercentage: parseFloat(form.btwPercentage),
        betaald: !!form.betaald,
        betaaldatum: form.betaald ? (form.betaaldatum || form.datum) : '',
      });
      // Bon-bijlage opslaan (gescande afbeelding of handmatig gekozen bestand)
      try {
        if (scannedImage) await carsysApi.uploadKostBijlageBase64(r.id, scannedImage, 'gescande-bon');
        else if (bijlageFile) await carsysApi.uploadKostBijlage(r.id, bijlageFile);
        if (scannedImage || bijlageFile) await refreshAll(true);
      } catch (be) { toast.warning(t('Kostenpost opgeslagen, maar bijlage uploaden mislukt', 'Kostenpost opgeslagen, maar bijlage uploaden mislukt'), { description: be.response?.data?.detail || be.message }); }
      toast.success(t('Kostenpost toegevoegd', 'Kostenpost toegevoegd'));
      setAddOpen(false);
      setForm(leegFormulier());
      setScannedImage(null);
      setBijlageFile(null);
    } catch (e) { toast.error(t('Opslaan mislukt', 'Opslaan mislukt'), { description: e.response?.data?.detail || e.message }); }
  };

  const handleUpdate = async () => {
    const excl = parseFloat(editKost.bedragExcl);
    if (!editKost.leverancier?.trim()) { toast.error(t('Vul een leverancier in', 'Vul een leverancier in')); return; }
    if (!excl || excl <= 0) { toast.error(t('Vul een geldig bedrag (excl. BTW) in', 'Vul een geldig bedrag (excl. BTW) in')); return; }
    try {
      await updateKost(editKost.id, {
        datum: editKost.datum,
        leverancier: editKost.leverancier.trim(),
        categorie: editKost.categorie,
        omschrijving: (editKost.omschrijving || '').trim(),
        factuurnummer: (editKost.factuurnummer || '').trim(),
        bedragExcl: excl,
        btwPercentage: parseFloat(editKost.btwPercentage),
        betaald: !!editKost.betaald,
        betaaldatum: editKost.betaald ? (editKost.betaaldatum || editKost.datum) : '',
      });
      toast.success(t('Opgeslagen', 'Opgeslagen'));
      setEditKost(null);
    } catch (e) { toast.error(t('Opslaan mislukt', 'Opslaan mislukt'), { description: e.response?.data?.detail || e.message }); }
  };

  const uploadBijlageNu = async (kid, file) => {
    if (!file) return;
    const ext = (file.name || '').split('.').pop().toLowerCase();
    if (!['jpg', 'jpeg', 'png', 'webp', 'pdf'].includes(ext)) { toast.error(t('Alleen JPG, PNG, WEBP of PDF', 'Alleen JPG, PNG, WEBP of PDF')); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error(t('Bestand te groot (max 10MB)', 'Bestand te groot (max 10MB)')); return; }
    setBijlageBusy(true);
    try {
      const res = await carsysApi.uploadKostBijlage(kid, file);
      setEditKost(prev => prev ? { ...prev, bijlage: res.bijlage } : prev);
      await refreshAll(true);
      toast.success(t('Bijlage toegevoegd', 'Bijlage toegevoegd'));
    } catch (e) { toast.error(t('Upload mislukt', 'Upload mislukt'), { description: e.response?.data?.detail || e.message }); }
    finally { setBijlageBusy(false); }
  };

  const bekijkBijlage = async (kid) => {
    try {
      const blob = await carsysApi.getKostBijlage(kid);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(t('Bijlage openen mislukt', 'Bijlage openen mislukt'), { description: e.response?.data?.detail || e.message }); }
  };

  const verwijderBijlage = async (kid) => {
    if (!(await confirmDialog(t('Bijlage verwijderen?', 'Bijlage verwijderen?')))) return;
    setBijlageBusy(true);
    try {
      await carsysApi.deleteKostBijlage(kid);
      setEditKost(prev => prev ? { ...prev, bijlage: null } : prev);
      await refreshAll(true);
      toast.success(t('Bijlage verwijderd', 'Bijlage verwijderd'));
    } catch (e) { toast.error(t('Verwijderen mislukt', 'Verwijderen mislukt'), { description: e.response?.data?.detail || e.message }); }
    finally { setBijlageBusy(false); }
  };

  const handleDelete = async (id) => {
    const targetId = id || selectedId;
    const k = kosten.find(x => x.id === targetId);
    if (!k) { toast.error(t('Kies een kostenpost om te verwijderen', 'Kies een kostenpost om te verwijderen')); return; }
    if (!(await confirmDialog(`${t('Kostenpost verwijderen?', 'Kostenpost verwijderen?')} (${k.leverancier} - ${eur(k.bedragIncl)})`))) return;
    try {
      await deleteKost(targetId);
      toast.success(t('Kostenpost verwijderd', 'Kostenpost verwijderd'));
      setSelectedId(null);
    } catch (e) { toast.error(t('Verwijderen mislukt', 'Verwijderen mislukt'), { description: e.response?.data?.detail || e.message }); }
  };

  const toggleBetaald = async (k) => {
    try {
      await updateKost(k.id, {
        ...k,
        betaald: !k.betaald,
        betaaldatum: !k.betaald ? new Date().toISOString().slice(0, 10) : '',
      });
    } catch (e) { toast.error(t('Wijzigen mislukt', 'Wijzigen mislukt'), { description: e.response?.data?.detail || e.message }); }
  };

  return (
    <div data-testid="kosten-page">
      <div className="cs-page-header">
        <div><h2>{t('Kosten / Inkoopfacturen', 'Kosten / Inkoopfacturen')}</h2><p>{t('Registreer je uitgaven en inkoopfacturen voor je boekhouding en BTW-aangifte', 'Registreer je uitgaven en inkoopfacturen voor je boekhouding en BTW-aangifte')}</p></div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input ref={fileInputRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handleScan} data-testid="kost-scan-input" />
          <button className="cs-btn" data-testid="btn-scan-bon" disabled={scanning} onClick={() => fileInputRef.current?.click()}>
            {scanning ? <Loader2 size={14} className="animate-spin" /> : <ScanLine size={14} />} {t('Scan bon (AI)', 'Scan bon (AI)')}
          </button>
          <button className="cs-btn primary" data-testid="btn-add-kost" onClick={() => { setForm(leegFormulier()); setScannedImage(null); setBijlageFile(null); setAddOpen(true); }}><Plus size={14} /> {t('Kostenpost toevoegen', 'Kostenpost toevoegen')}</button>
          <button className="cs-btn danger" data-testid="btn-delete-kost" disabled={!selectedId} onClick={() => handleDelete()}><Trash2 size={14} /> {t('Verwijderen', 'Verwijderen')}</button>
        </div>
      </div>

      <div className="cs-three-col" style={{ marginBottom: 14 }}>
        <div className="cs-kpi" data-testid="kpi-kosten-totaal"><div className="label">{t('Totaal kosten (incl. BTW)', 'Totaal kosten (incl. BTW)')} · {jaar}</div><div className="value" style={{ color: 'var(--cs-red)' }}>{eur(totaalIncl)}</div></div>
        <div className="cs-kpi" data-testid="kpi-kosten-btw"><div className="label">{t('BTW voorbelasting (terug te vragen)', 'BTW voorbelasting (terug te vragen)')} · {jaar}</div><div className="value" style={{ color: 'var(--cs-accent)' }}>{eur(totaalBtw)}</div></div>
        <div className="cs-kpi" data-testid="kpi-kosten-onbetaald"><div className="label">{t('Nog te betalen', 'Nog te betalen')} ({onbetaald.length})</div><div className="value" style={{ color: '#f59e0b' }}>{eur(onbetaaldBedrag)}</div></div>
      </div>

      <div className="cs-toolbar" style={{ display: 'flex', gap: 8 }}>
        <div className="grow" style={{ position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: 'var(--cs-text-muted)' }} />
          <input className="cs-input" data-testid="kosten-search" style={{ paddingLeft: 30 }} placeholder={t('Zoeken op leverancier, omschrijving, factuurnr...', 'Zoeken op leverancier, omschrijving, factuurnr...')} value={filter} onChange={e => setFilter(e.target.value)} />
        </div>
        <select className="cs-input" data-testid="kosten-cat-filter" style={{ maxWidth: 200 }} value={catFilter} onChange={e => setCatFilter(e.target.value)}>
          <option value="alle">{t('Alle categorieën', 'Alle categorieën')}</option>
          {KOSTEN_CATEGORIEEN.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <input className="cs-input" type="date" style={{ maxWidth: 150 }} title={t('Van datum', 'Van datum')} data-testid="kosten-van-datum" value={vanDatum} onChange={e => setVanDatum(e.target.value)} />
        <input className="cs-input" type="date" style={{ maxWidth: 150 }} title={t('Tot datum', 'Tot datum')} data-testid="kosten-tot-datum" value={totDatum} onChange={e => setTotDatum(e.target.value)} />
        <button className={`cs-btn ${alleenBijlage ? 'primary' : ''}`} data-testid="kosten-bijlage-filter" onClick={() => setAlleenBijlage(v => !v)} title={t('Alleen met bon-bijlage', 'Alleen met bon-bijlage')}><Paperclip size={14} /> {t('Met bon', 'Met bon')}</button>
      </div>

      <div className="cs-card">
        <table className="cs-table">
          <thead><tr>
            <th>{t('Datum', 'Datum')}</th>
            <th>{t('Leverancier', 'Leverancier')}</th>
            <th>{t('Categorie', 'Categorie')}</th>
            <th>{t('Omschrijving', 'Omschrijving')}</th>
            <th>{t('Factuurnr.', 'Factuurnr.')}</th>
            <th style={{ textAlign: 'right' }}>{t('Excl. BTW', 'Excl. BTW')}</th>
            <th style={{ textAlign: 'right' }}>{t('BTW', 'BTW')}</th>
            <th style={{ textAlign: 'right' }}>{t('Incl. BTW', 'Incl. BTW')}</th>
            <th style={{ textAlign: 'center' }}>{t('Betaald', 'Betaald')}</th>
            <th></th>
          </tr></thead>
          <tbody>
            {filtered.map(k => (
              <tr key={k.id} className={k.id === selectedId ? 'selected' : ''} onClick={() => setSelectedId(k.id)} onDoubleClick={() => setEditKost({ ...k })} style={{ cursor: 'pointer' }} title={t('Dubbelklik om te bewerken', 'Dubbelklik om te bewerken')} data-testid={`kost-row-${k.id}`}>
                <td>{k.datum ? new Date(k.datum).toLocaleDateString('nl-NL') : '-'}</td>
                <td style={{ fontWeight: 600 }}>
                  {k.leverancier}
                  {k.bijlage && <button className="cs-btn ghost" style={{ padding: 2, marginLeft: 6 }} title={t('Bon bekijken', 'Bon bekijken')} data-testid={`kost-bijlage-icon-${k.id}`} onClick={(e) => { e.stopPropagation(); bekijkBijlage(k.id); }}><Paperclip size={12} style={{ verticalAlign: '-1px', color: 'var(--cs-accent)' }} /></button>}
                </td>
                <td><span className="cs-badge gray">{k.categorie}</span></td>
                <td style={{ color: 'var(--cs-text-muted)', fontSize: 12, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{k.omschrijving || '-'}</td>
                <td style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{k.factuurnummer || '-'}</td>
                <td style={{ textAlign: 'right' }}>{eur(k.bedragExcl)}</td>
                <td style={{ textAlign: 'right', color: 'var(--cs-text-muted)', fontSize: 12 }}>{eur(k.btwBedrag)} <span style={{ fontSize: 10 }}>({k.btwPercentage}%)</span></td>
                <td style={{ textAlign: 'right', fontWeight: 600 }}>{eur(k.bedragIncl)}</td>
                <td style={{ textAlign: 'center' }}>
                  <button className={`cs-badge ${k.betaald ? 'green' : 'amber'}`} data-testid={`kost-betaald-${k.id}`} style={{ cursor: 'pointer', border: 'none' }} onClick={(e) => { e.stopPropagation(); toggleBetaald(k); }} title={t('Klik om status te wisselen', 'Klik om status te wisselen')}>
                    {k.betaald ? <><Check size={11} style={{ verticalAlign: '-1px' }} /> {t('Betaald', 'Betaald')}</> : <><X size={11} style={{ verticalAlign: '-1px' }} /> {t('Open', 'Open')}</>}
                  </button>
                </td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button className="cs-btn ghost" style={{ padding: 4 }} title={t('Bewerken', 'Bewerken')} data-testid={`edit-kost-${k.id}`} onClick={(e) => { e.stopPropagation(); setEditKost({ ...k }); }}><Edit size={11} /></button>
                  <button className="cs-btn ghost danger" style={{ padding: 4 }} title={t('Verwijderen', 'Verwijderen')} data-testid={`del-kost-${k.id}`} onClick={(e) => { e.stopPropagation(); handleDelete(k.id); }}><Trash2 size={11} /></button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={10} className="cs-empty">{t('Nog geen kosten geregistreerd. Klik op "Kostenpost toevoegen" om je eerste inkoopfactuur in te voeren.', 'Nog geen kosten geregistreerd. Klik op "Kostenpost toevoegen" om je eerste inkoopfactuur in te voeren.')}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {(addOpen || editKost) && (() => {
        const isEdit = !!editKost;
        const data = isEdit ? editKost : form;
        const setData = isEdit ? (upd) => setEditKost(prev => ({ ...prev, ...upd })) : (upd) => setForm(prev => ({ ...prev, ...upd }));
        const pv = preview(data.bedragExcl, data.btwPercentage);
        return (
          <div className="cs-modal-overlay" onClick={() => { setAddOpen(false); setEditKost(null); setScannedImage(null); setBijlageFile(null); }}>
            <div className="cs-modal" onClick={e => e.stopPropagation()}>
              <h3 style={{ marginTop: 0 }}>{isEdit ? t('Kostenpost bewerken', 'Kostenpost bewerken') : t('Kostenpost toevoegen', 'Kostenpost toevoegen')}</h3>
              <div style={{ display: 'grid', gap: 10 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div><label className="cs-section-title">{t('Datum', 'Datum')}</label><input type="date" className="cs-input" data-testid="kost-datum" value={(data.datum || '').slice(0, 10)} onChange={e => setData({ datum: e.target.value })} /></div>
                  <div><label className="cs-section-title">{t('Categorie', 'Categorie')}</label>
                    <select className="cs-input" data-testid="kost-categorie" value={data.categorie} onChange={e => setData({ categorie: e.target.value })}>
                      {KOSTEN_CATEGORIEEN.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div><label className="cs-section-title">{t('Leverancier', 'Leverancier')}</label><input className="cs-input" data-testid="kost-leverancier" placeholder={t('bv. Bosch, Shell, verhuurder', 'bv. Bosch, Shell, verhuurder')} value={data.leverancier} onChange={e => setData({ leverancier: e.target.value })} /></div>
                  <div><label className="cs-section-title">{t('Factuurnummer', 'Factuurnummer')} <span style={{ fontSize: 10, color: 'var(--cs-text-muted)', fontWeight: 400 }}>({t('optioneel', 'optioneel')})</span></label><input className="cs-input" data-testid="kost-factuurnummer" value={data.factuurnummer} onChange={e => setData({ factuurnummer: e.target.value })} /></div>
                </div>
                <div><label className="cs-section-title">{t('Omschrijving', 'Omschrijving')}</label><input className="cs-input" data-testid="kost-omschrijving" value={data.omschrijving} onChange={e => setData({ omschrijving: e.target.value })} /></div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div><label className="cs-section-title">{t('Bedrag excl. BTW (€)', 'Bedrag excl. BTW (€)')}</label><input type="number" step="0.01" className="cs-input" data-testid="kost-bedrag" value={data.bedragExcl} onChange={e => setData({ bedragExcl: e.target.value })} placeholder="0,00" /></div>
                  <div><label className="cs-section-title">{t('BTW-tarief', 'BTW-tarief')}</label>
                    <select className="cs-input" data-testid="kost-btw" value={data.btwPercentage} onChange={e => setData({ btwPercentage: parseFloat(e.target.value) })}>
                      <option value={21}>21%</option><option value={9}>9%</option><option value={0}>0% / {t('geen BTW', 'geen BTW')}</option>
                    </select>
                  </div>
                </div>
                <div style={{ fontSize: 12, color: 'var(--cs-text-muted)', padding: '8px 10px', background: 'var(--cs-card-alt)', borderRadius: 6 }} data-testid="kost-preview">
                  {t('BTW', 'BTW')}: <b>{eur(pv.btw)}</b> · {t('Totaal incl. BTW', 'Totaal incl. BTW')}: <b style={{ color: 'var(--cs-text)' }}>{eur(pv.incl)}</b>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                  <input type="checkbox" data-testid="kost-betaald-check" checked={!!data.betaald} onChange={e => setData({ betaald: e.target.checked })} />
                  {t('Reeds betaald', 'Reeds betaald')}
                </label>

                <div style={{ borderTop: '1px solid var(--cs-border)', paddingTop: 10 }}>
                  <label className="cs-section-title"><Paperclip size={12} style={{ verticalAlign: -1, marginRight: 4 }} />{t('Bon-bijlage', 'Bon-bijlage')}</label>
                  {isEdit && data.bijlage ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--cs-card-alt)', borderRadius: 6, padding: '8px 10px', fontSize: 13 }} data-testid="kost-bijlage-huidig">
                      <Paperclip size={14} />
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{data.bijlage.filename}</span>
                      <button type="button" className="cs-btn ghost" onClick={() => bekijkBijlage(data.id)} data-testid="kost-bijlage-bekijk"><Eye size={14} /> {t('Bekijk', 'Bekijk')}</button>
                      <button type="button" className="cs-btn ghost" disabled={bijlageBusy} onClick={() => verwijderBijlage(data.id)} data-testid="kost-bijlage-verwijder"><Trash2 size={14} /></button>
                    </div>
                  ) : isEdit ? (
                    <>
                      <input ref={bijlageInputRef} type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) uploadBijlageNu(data.id, f); }} data-testid="kost-bijlage-edit-input" />
                      <button type="button" className="cs-btn" disabled={bijlageBusy} onClick={() => bijlageInputRef.current?.click()} data-testid="kost-bijlage-edit-upload">
                        {bijlageBusy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {t('Bon uploaden', 'Bon uploaden')}
                      </button>
                    </>
                  ) : (
                    <>
                      <input type="file" accept="image/*,application/pdf" onChange={e => setBijlageFile(e.target.files?.[0] || null)} data-testid="kost-bijlage-add-input" style={{ fontSize: 13 }} />
                      {scannedImage && !bijlageFile && <div style={{ fontSize: 12, color: 'var(--cs-green)', marginTop: 4 }}>📎 {t('Gescande bon wordt automatisch als bijlage opgeslagen.', 'Gescande bon wordt automatisch als bijlage opgeslagen.')}</div>}
                      <div style={{ fontSize: 11, color: 'var(--cs-text-muted)', marginTop: 4 }}>{t('JPG, PNG, WEBP of PDF · max 10MB', 'JPG, PNG, WEBP of PDF · max 10MB')}</div>
                    </>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
                <button className="cs-btn" onClick={() => { setAddOpen(false); setEditKost(null); setScannedImage(null); setBijlageFile(null); }}>{t('Annuleren', 'Annuleren')}</button>
                <button className="cs-btn primary" data-testid="kost-save" onClick={isEdit ? handleUpdate : handleSave}>{t('Opslaan', 'Opslaan')}</button>
              </div>
            </div>
          </div>
        );
      })()}

      <style>{`
        .cs-modal-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.5); display: flex; align-items: center; justify-content: center; z-index: 100; }
        .cs-modal { background: #fff; border-radius: 10px; padding: 20px 22px; width: 560px; max-width: 92vw; box-shadow: 0 20px 60px rgba(0,0,0,0.25); }
      `}</style>
    </div>
  );
}
